import React, { useState, useEffect, useCallback } from 'react';
import { useShop } from '../contexts/ShopContext';
import { ShopLocation, Place, Slide } from '../types';
import { shopLocationService } from '../services/shopLocationService';
import { slideApiService } from '../services/slideApiService';
import LocationCard from './LocationCard';
import PlaceCard from './PlaceCard';
import CompactSlideCard from './CompactSlideCard';
import { invalidateLocationCache } from './SlideCard';
import LocationEditModal from './LocationEditModal';
import PlaceEditModal from './PlaceEditModal';
import SlideEditModal from './SlideEditModal';

interface LocationsTabProps {
  shopId: string;
}

const LocationsTab: React.FC<LocationsTabProps> = ({ shopId }) => {
  const { canManageShop } = useShop();

  // Data state
  const [locations, setLocations] = useState<ShopLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<ShopLocation | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [slides, setSlides] = useState<Slide[]>([]);

  // Loading and error states
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingSlides, setLoadingSlides] = useState(false);
  const [error, setError] = useState('');

  // Edit modal state
  const [editingLocation, setEditingLocation] = useState<ShopLocation | null>(null);
  const [editingPlace, setEditingPlace] = useState<Place | null>(null);
  const [editingSlide, setEditingSlide] = useState<Slide | null>(null);

  // Collapse state for each row - collapse empty rows by default
  // Start all rows expanded - useEffect hooks will auto-collapse empty rows after data loads
  const [locationsCollapsed, setLocationsCollapsed] = useState(false);
  const [placesCollapsed, setPlacesCollapsed] = useState(false);
  const [slidesCollapsed, setSlidesCollapsed] = useState(false);

  const loadLocations = useCallback(async () => {
    try {
      setLoadingLocations(true);
      setError('');
      const data = await shopLocationService.getShopLocations(shopId);
      setLocations(data);
      return data; // Return fresh data to avoid race conditions
    } catch (error) {
      console.error('Error loading locations:', error);
      setError('Failed to load shop locations');
      return []; // Return empty array on error
    } finally {
      setLoadingLocations(false);
    }
  }, [shopId]);

  // Reset all state and reload when shopId changes
  useEffect(() => {
    setSelectedLocation(null);
    setSelectedPlace(null);
    setSlides([]);
    setError('');
    setEditingLocation(null);
    setEditingPlace(null);
    setEditingSlide(null);
    loadLocations();
  }, [loadLocations]);

  // Load slides when place or location is selected
  useEffect(() => {
    const loadSlides = async () => {
      try {
        setLoadingSlides(true);

        if (selectedPlace) {
          // Load slides for specific place
          const result = await slideApiService.searchSlides({
            shopId,
            placeId: selectedPlace.id,
            page: 0,
            size: 100
          });
          setSlides(result.items || []);
        } else if (selectedLocation) {
          // Load slides for location (not in a specific place)
          const result = await slideApiService.searchSlides({
            shopId,
            locationId: selectedLocation.id,
            page: 0,
            size: 100
          });
          setSlides(result.items || []);
        } else {
          setSlides([]);
        }
      } catch (error) {
        console.error('Error loading slides:', error);
        setSlides([]);
      } finally {
        setLoadingSlides(false);
      }
    };

    loadSlides();
  }, [selectedPlace, selectedLocation]);

  // Auto-collapse empty rows - track if initial load has completed
  const hasLoadedLocations = React.useRef(false);
  const prevLocationsLength = React.useRef<number>(0);
  const prevPlacesLength = React.useRef<number>(0);
  const prevSlidesLength = React.useRef<number>(0);

  useEffect(() => {
    // After initial locations load, collapse if empty
    if (!hasLoadedLocations.current && !loadingLocations && locations.length >= 0) {
      hasLoadedLocations.current = true;
      if (locations.length === 0) {
        setLocationsCollapsed(true);
      }
    }
    // Also collapse when transitioning from content to empty
    if (prevLocationsLength.current > 0 && locations.length === 0) {
      setLocationsCollapsed(true);
    }
    prevLocationsLength.current = locations.length;
  }, [locations.length, loadingLocations]);

  useEffect(() => {
    // Auto-collapse/expand places row based on content
    const selectedLocationPlaces = selectedLocation?.places || [];
    if (selectedLocationPlaces.length === 0) {
      setPlacesCollapsed(true);
    } else if (prevPlacesLength.current === 0 && selectedLocationPlaces.length > 0) {
      // Auto-expand when transitioning from empty to having content
      setPlacesCollapsed(false);
    }
    prevPlacesLength.current = selectedLocationPlaces.length;
  }, [selectedLocation?.places?.length, selectedLocation]);

  useEffect(() => {
    // Auto-collapse/expand slides row based on content
    if (slides.length === 0) {
      setSlidesCollapsed(true);
    } else if (prevSlidesLength.current === 0 && slides.length > 0) {
      // Auto-expand when transitioning from empty to having content
      setSlidesCollapsed(false);
    }
    prevSlidesLength.current = slides.length;
  }, [slides.length]);

  const handleLocationSelect = (location: ShopLocation) => {
    if (selectedLocation?.id === location.id) {
      // Deselect if clicking same location
      setSelectedLocation(null);
      setSelectedPlace(null);
    } else {
      setSelectedLocation(location);
      setSelectedPlace(null); // Reset place selection when location changes
    }
  };

  const handlePlaceSelect = (place: Place) => {
    if (selectedPlace?.id === place.id) {
      // Deselect if clicking same place
      setSelectedPlace(null);
    } else {
      setSelectedPlace(place);
    }
  };

  const handlePlaceDrop = async (placeId: string, fromLocationId: string, toLocationId: string) => {
    try {
      setError('');

      // Call the move place API
      await shopLocationService.movePlace(shopId, placeId, fromLocationId, toLocationId);
      invalidateLocationCache(shopId);

      // Refresh locations to show updated state
      const refreshedLocations = await shopLocationService.getShopLocations(shopId);
      setLocations(refreshedLocations);

      // Auto-select the destination location to show where the place was moved
      const toLocation = refreshedLocations.find(loc => loc.id === toLocationId);
      if (toLocation) {
        setSelectedLocation(toLocation);

        // Auto-select the moved place in the destination location
        const movedPlace = toLocation.places.find(p => p.id === placeId);
        if (movedPlace) {
          setSelectedPlace(movedPlace);
        }
      }

    } catch (error) {
      console.error('Error moving place:', error);
      setError('Failed to move place. Please try again.');
    }
  };

  const getPlacesForSelectedLocation = (): Place[] => {
    return selectedLocation?.places || [];
  };

  const handleLocationEdit = (location: ShopLocation) => {
    setEditingLocation(location);
  };

  const handleLocationUpdated = async (updatedLocation: ShopLocation) => {
    invalidateLocationCache(shopId);
    // Refresh locations and get the fresh data
    const refreshedLocations = await loadLocations();

    // Update selected location using the fresh data
    if (selectedLocation?.id === updatedLocation.id) {
      const refreshedLocation = refreshedLocations.find(loc => loc.id === updatedLocation.id);
      if (refreshedLocation) {
        setSelectedLocation(refreshedLocation);
      }
    }
  };

  const handlePlaceEdit = (place: Place) => {
    setEditingPlace(place);
  };

  const getSlideCountForPlace = (placeId: string): number => {
    return slides.filter(slide => slide.placeId === placeId).length;
  };

  const handleLocationDelete = async (location: ShopLocation) => {
    try {
      setError('');
      await shopLocationService.deleteShopLocation(shopId, location.id);
      invalidateLocationCache(shopId);

      // Refresh locations to show updated state
      await loadLocations();

      // Clear selected location if it was the one deleted
      if (selectedLocation?.id === location.id) {
        setSelectedLocation(null);
        setSelectedPlace(null);
      }
    } catch (error: any) {
      console.error('Error deleting location:', error);

      // Check if error response has a specific message header
      if (error.response?.headers?.['x-error-message']) {
        setError(error.response.headers['x-error-message']);
      } else {
        setError('Failed to delete location. Please try again.');
      }
    }
  };

  const handlePlaceDelete = async (place: Place) => {
    if (!selectedLocation) return;

    try {
      setError('');
      await shopLocationService.deletePlace(shopId, selectedLocation.id, place.id);
      invalidateLocationCache(shopId);

      // Refresh locations to show updated state
      const refreshedLocations = await loadLocations();

      // Update selected location using fresh data
      const refreshedLocation = refreshedLocations.find(loc => loc.id === selectedLocation.id);
      if (refreshedLocation) {
        setSelectedLocation(refreshedLocation);
      }

      // Clear selected place if it was the one deleted
      if (selectedPlace?.id === place.id) {
        setSelectedPlace(null);
      }
    } catch (error: any) {
      console.error('Error deleting place:', error);

      // Check if error response has a specific message header
      if (error.response?.headers?.['x-error-message']) {
        setError(error.response.headers['x-error-message']);
      } else {
        setError('Failed to delete place. Please try again.');
      }
    }
  };

  const handlePlaceUpdated = async () => {
    invalidateLocationCache(shopId);
    // Refresh locations and get the fresh data
    const refreshedLocations = await loadLocations();

    // Update selected location to reflect place changes using fresh data
    if (selectedLocation) {
      const refreshedLocation = refreshedLocations.find(loc => loc.id === selectedLocation.id);
      if (refreshedLocation) {
        setSelectedLocation(refreshedLocation);

        // Also update selected place if it exists
        if (selectedPlace) {
          const refreshedPlace = refreshedLocation.places.find(p => p.id === selectedPlace.id);
          if (refreshedPlace) {
            setSelectedPlace(refreshedPlace);
          }
        }
      }
    }
  };

  const handleSlideEdit = (slide: Slide) => {
    // Use the freshest slide data from state to ensure cache busters are current
    const freshSlide = slides.find(s => s.id === slide.id) || slide;
    setEditingSlide(freshSlide);
  };

  const handleSlideUpdated = async (updatedSlide: Slide) => {
    // Refresh the slides list to show updated data
    if (selectedPlace) {
      const result = await slideApiService.searchSlides({
        shopId,
        placeId: selectedPlace.id,
        page: 0,
        size: 100
      });
      setSlides(result.items || []);
    } else if (selectedLocation) {
      const result = await slideApiService.searchSlides({
        shopId,
        locationId: selectedLocation.id,
        page: 0,
        size: 100
      });
      setSlides(result.items || []);
    }
  };

  const handleSlideDropOnLocation = async (slideId: string, locationId: string) => {
    try {
      setError('');

      // Fetch the current slide to get all its data
      const currentSlide = await slideApiService.getSlideById(shopId, slideId);

      // Build update data matching SlideEditModal pattern - include images and priceHistory
      const updateData: any = {
        name: currentSlide.name,
        description: currentSlide.description,
        categories: currentSlide.categories,
        shopId: currentSlide.shopId,
        images: currentSlide.images || [],
        priceHistory: currentSlide.priceHistory || [],
        active: currentSlide.active,
        locationId: locationId
        // Omit placeId to clear the association
      };

      await slideApiService.updateSlide(shopId, slideId, updateData);

      // Refresh slides list to reflect the change
      if (selectedPlace) {
        const result = await slideApiService.searchSlides({
          shopId,
          placeId: selectedPlace.id,
          page: 0,
          size: 100
        });
        setSlides(result.items || []);
      } else if (selectedLocation) {
        const result = await slideApiService.searchSlides({
          shopId,
          locationId: selectedLocation.id,
          page: 0,
          size: 100
        });
        setSlides(result.items || []);
      }
    } catch (error) {
      console.error('Error moving slide to location:', error);
      setError('Failed to move item to location. Please try again.');
    }
  };

  const handleSlideDropOnPlace = async (slideId: string, placeId: string) => {
    try {
      setError('');

      // Find the place to get its location ID
      if (!selectedLocation) {
        setError('No location selected');
        return;
      }

      const targetPlace = selectedLocation.places.find(p => p.id === placeId);
      if (!targetPlace) {
        setError('Target place not found');
        return;
      }

      // Fetch the current slide to get all its data
      const currentSlide = await slideApiService.getSlideById(shopId, slideId);

      // Build update data matching SlideEditModal pattern - include images and priceHistory
      // locationId and placeId are mutually exclusive - placeId implies the location
      const updateData: any = {
        name: currentSlide.name,
        description: currentSlide.description,
        categories: currentSlide.categories,
        shopId: currentSlide.shopId,
        images: currentSlide.images || [],
        priceHistory: currentSlide.priceHistory || [],
        active: currentSlide.active,
        placeId: placeId
        // Omit locationId when setting placeId - they are mutually exclusive
      };

      await slideApiService.updateSlide(shopId, slideId, updateData);

      // Auto-select the target place to show the updated state
      if (selectedPlace?.id !== placeId) {
        // If we're moving to a different place, select it
        setSelectedPlace(targetPlace);
      }

      // Refresh slides list for the target place
      const result = await slideApiService.searchSlides({
        shopId,
        placeId: placeId,
        page: 0,
        size: 100
      });
      setSlides(result.items || []);
    } catch (error) {
      console.error('Error moving slide to place:', error);
      setError('Failed to move item to place. Please try again.');
    }
  };

  return (
    <div style={{ minHeight: '600px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h3 className="heading-secondary text-lg">Shop Locations</h3>
        <p className="text-muted text-sm">
          Manage physical locations where your inventory is stored
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div
          style={{
            padding: '16px',
            marginBottom: '24px',
            backgroundColor: 'var(--error-bg)',
            border: '1px solid var(--error-border)',
            borderRadius: '8px',
            color: 'var(--error-text)'
          }}
        >
          {error}
        </div>
      )}

      {/* 3-Row Layout */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

        {/* Row 1: Locations */}
        <div style={{
          border: '1px solid var(--border-light)',
          borderRadius: '8px',
          backgroundColor: 'var(--bg-card)'
        }}>
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '8px 8px 0 0',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--text-primary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setLocationsCollapsed(!locationsCollapsed)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-primary)',
                  transition: 'transform 0.2s ease'
                }}
                title={locationsCollapsed ? 'Expand Locations' : 'Collapse Locations'}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  style={{
                    transform: locationsCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                >
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <span>Locations ({locations.length})</span>
            </div>
            {canManageShop && (
              <button
                onClick={() => setEditingLocation({} as any)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  border: 'none',
                  borderRadius: '3px',
                  backgroundColor: 'var(--primary)',
                  color: 'var(--text-inverse)',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--error)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--primary)';
                }}
                title="Create new location"
              >
                <span>+</span>
                <span>Create</span>
              </button>
            )}
          </div>

          {!locationsCollapsed && (
            <div style={{ padding: '16px' }}>
              {loadingLocations ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-secondary)' }}>
                  <div style={{ marginBottom: '12px' }}>Loading locations...</div>
                </div>
              ) : locations.length === 0 ? (
                <div
                  style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    backgroundColor: 'var(--bg-card)',
                    border: '2px dashed var(--border-light)',
                    borderRadius: '8px'
                  }}
                >
                  <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                    No locations yet
                  </p>
                  <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                    Create your first location to get started
                  </p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: '8px'
                }}>
                  {locations.map((location) => (
                    <LocationCard
                      key={location.id}
                      location={location}
                      shopId={shopId}
                      isSelected={selectedLocation?.id === location.id}
                      onClick={() => handleLocationSelect(location)}
                      onPlaceDrop={handlePlaceDrop}
                      onSlideDrop={handleSlideDropOnLocation}
                      onEdit={canManageShop ? handleLocationEdit : undefined}
                      onDragHoverSelect={(location) => {
                        // Auto-select location when user hovers while dragging
                        setSelectedLocation(location);
                        setSelectedPlace(null); // Clear place selection to show the hovered location's places
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Row 2: Places */}
        <div style={{
          border: '1px solid var(--border-light)',
          borderRadius: '8px',
          backgroundColor: 'var(--bg-card)'
        }}>
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '8px 8px 0 0',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--text-primary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setPlacesCollapsed(!placesCollapsed)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-primary)',
                  transition: 'transform 0.2s ease'
                }}
                title={placesCollapsed ? 'Expand Places' : 'Collapse Places'}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  style={{
                    transform: placesCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                >
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <span>Places ({selectedLocation ? selectedLocation.places.length : 0})</span>
            </div>
            {canManageShop && selectedLocation && (
              <button
                onClick={() => setEditingPlace({} as any)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  border: 'none',
                  borderRadius: '3px',
                  backgroundColor: 'var(--primary)',
                  color: 'var(--text-inverse)',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--error)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--primary)';
                }}
                title="Create new place"
              >
                <span>+</span>
                <span>Create</span>
              </button>
            )}
          </div>

          {!placesCollapsed && (
            <div style={{ padding: '16px' }}>
              {!selectedLocation ? (
                <div
                  style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    backgroundColor: 'var(--bg-card)',
                    border: '2px dashed var(--border-light)',
                    borderRadius: '8px'
                  }}
                >
                  <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                    Select a location
                  </p>
                  <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                    Choose a location to view its places
                  </p>
                </div>
              ) : getPlacesForSelectedLocation().length === 0 ? (
                <div
                  style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    backgroundColor: 'var(--bg-card)',
                    border: '2px dashed var(--border-light)',
                    borderRadius: '8px'
                  }}
                >
                  <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📦</div>
                  <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                    No places in {selectedLocation.name}
                  </p>
                  <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                    Add places to organize items within this location
                  </p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: '8px'
                }}>
                  {getPlacesForSelectedLocation().map((place) => (
                    <PlaceCard
                      key={place.id}
                      place={place}
                      shopId={shopId}
                      locationId={selectedLocation.id}
                      isSelected={selectedPlace?.id === place.id}
                      onClick={() => handlePlaceSelect(place)}
                      onSlideDrop={handleSlideDropOnPlace}
                      onEdit={canManageShop ? handlePlaceEdit : undefined}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Row 3: Slides */}
        <div style={{
          border: '1px solid var(--border-light)',
          borderRadius: '8px',
          backgroundColor: 'var(--bg-card)'
        }}>
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '8px 8px 0 0',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--text-primary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <button
              onClick={() => setSlidesCollapsed(!slidesCollapsed)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--text-primary)',
                transition: 'transform 0.2s ease'
              }}
              title={slidesCollapsed ? 'Expand Slides' : 'Collapse Slides'}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                style={{
                  transform: slidesCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <span>Slides ({slides.length})</span>
          </div>

          {!slidesCollapsed && (
            <div style={{ padding: '16px' }}>
              {!selectedPlace && !selectedLocation ? (
                <div
                  style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    backgroundColor: 'var(--bg-card)',
                    border: '2px dashed var(--border-light)',
                    borderRadius: '8px'
                  }}
                >
                  <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                    Select a location or place
                  </p>
                  <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                    Choose a location or place to view its items
                  </p>
                </div>
              ) : loadingSlides ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-secondary)' }}>
                  <div style={{ marginBottom: '12px' }}>Loading items...</div>
                </div>
              ) : slides.length === 0 ? (
                <div
                  style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    backgroundColor: 'var(--bg-card)',
                    border: '2px dashed var(--border-light)',
                    borderRadius: '8px'
                  }}
                >
                  <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🖼️</div>
                  <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                    No items in {selectedPlace?.name || selectedLocation?.name}
                  </p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                  gap: '4px'
                }}>
                  {slides.map((slide) => (
                    <CompactSlideCard
                      key={slide.id}
                      slide={slide}
                      onSlideEdited={canManageShop ? handleSlideEdit : undefined}
                      onSlideUpdated={handleSlideUpdated}
                      onSlideDeleted={(slideId) => {
                        setSlides(prev => prev.filter(s => s.id !== slideId));
                      }}
                      enableDrag={canManageShop}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modals */}
      {editingLocation && (
        <LocationEditModal
          location={editingLocation}
          shopId={shopId}
          onClose={() => setEditingLocation(null)}
          onLocationUpdated={handleLocationUpdated}
        />
      )}

      {editingPlace && selectedLocation && (
        <PlaceEditModal
          place={editingPlace}
          shopId={shopId}
          locationId={selectedLocation.id}
          onClose={() => setEditingPlace(null)}
          onPlaceUpdated={handlePlaceUpdated}
        />
      )}

      {editingSlide && (
        <SlideEditModal
          slide={editingSlide}
          onClose={() => setEditingSlide(null)}
          onSlideUpdated={handleSlideUpdated}
        />
      )}

    </div>
  );
};

export default LocationsTab;
